import { isExperienceEvent } from '@/features/events/lib/event.lib';
import type { DecoratedEvent } from '@/features/events/types';
import type { PageExperience } from '@/features/experiences/types';
import {
  getDestination,
  getIntegrationDestination,
} from '@/features/interactions/addToCalendar/destinations/registry';
import type { PlatformExport } from '@/features/magic/hooks/usePlatformExports';
import { type EventExportPlatform, PlatformTypes } from '@/types/gql.generated';

export type ExportStatus = {
  isSuccess: boolean;
  isStale: boolean;
};

/**
 * The server `event.exports` row platform an integration destination writes,
 * declared by its module; undefined for every other destination.
 */
export const getIntegrationExportPlatform = (
  platform: PlatformTypes
): EventExportPlatform | undefined =>
  getIntegrationDestination(platform)?.integration.exportPlatform;

/** The event has an auto-updating export row for the provider. */
export const hasAutoUpdateExport = (
  event: Pick<DecoratedEvent, 'exports'>,
  platform: EventExportPlatform
): boolean =>
  event.exports.some((row) => row.platform === platform && row.autoUpdate);

/** The event has an auto-updating export row for any provider. */
export const hasAnyAutoUpdateExport = (
  event: Pick<DecoratedEvent, 'exports'>
): boolean => event.exports.some((row) => row.autoUpdate);

/**
 * One provider's verdict from the event's export rows — the same collapse the
 * server used to apply for the (Google-only, now deprecated) `exportState`:
 * no rows → not exported; any auto-updating row → success and never stale;
 * otherwise stale when any row lags the event (a null sequence can't prove
 * it's current).
 */
export const getIntegrationExportStatus = (
  event: Pick<DecoratedEvent, 'exports' | 'version'>,
  platform: EventExportPlatform,
  calendarId?: string
): ExportStatus => {
  const rows = event.exports.filter(
    (row) =>
      row.platform === platform &&
      (calendarId === undefined || row.calendarId === calendarId)
  );

  if (rows.length === 0) {
    return { isSuccess: false, isStale: false };
  }

  if (rows.some((row) => row.autoUpdate)) {
    return { isSuccess: true, isStale: false };
  }

  return {
    isSuccess: true,
    isStale: rows.some(
      (row) => row.sequence == null || row.sequence < event.version
    ),
  };
};

/**
 * Every experience id that counts as "the schedule being viewed" — the given
 * experiences plus their child views. An event's `exportedToExperiences` lists
 * a planner's views alongside real export targets (the views hold the same
 * events), so an added-elsewhere check has to discount the whole set, not just
 * the experience in hand.
 */
export const getViewedScheduleIds = (
  ...experiences: (PageExperience | undefined)[]
): Set<string> => {
  const ids = new Set<string>();
  experiences.forEach((experience) => {
    if (!experience) return;
    ids.add(experience.id);
    experience.children?.forEach((child) => ids.add(child.id));
  });
  return ids;
};

export const hasLocalExport = (
  platformExports: PlatformExport[],
  event: DecoratedEvent,
  platform: PlatformTypes,
  calendarId?: string
): boolean => {
  return platformExports.some(
    (item) =>
      item.platform === platform &&
      (calendarId === undefined || item.calendarId === calendarId) &&
      item.entryExports.some((e) => e.entry.id === event.id)
  );
};

/** The event was exported into this specific experience. */
export const hasExperienceExport = (
  event: DecoratedEvent,
  experienceId: string
): boolean =>
  isExperienceEvent(event) &&
  event.exportedToExperiences.some((exp) => exp.id === experienceId);

/**
 * The event was exported into some experience other than the one(s) being
 * viewed. Experience exports are live references and never go stale.
 */
export const hasExperienceExportElsewhere = (
  event: DecoratedEvent,
  viewedExperienceIds: Set<string>
): boolean =>
  isExperienceEvent(event) &&
  event.exportedToExperiences.some((exp) => !viewedExperienceIds.has(exp.id));

export type EventExportStatusInputs = {
  /**
   * Session-optimistic records — for server-backed platforms these bridge the
   * gap until the relevant query refetches.
   */
  platformExports: PlatformExport[];
  /**
   * Persisted local adds (localAdds.store): weblink platforms and Agenda Hero
   * adds that create detached copies, where no server record exists. The
   * optional destinationId scopes the lookup to one Agenda Hero destination.
   */
  hasLocalAdd: (
    eventId: string,
    platform: PlatformTypes,
    destinationId?: string
  ) => boolean;
  didSubscribeToEvent: (eventId: string) => boolean;
  /**
   * The reader email-subscribed to the whole schedule being viewed — the
   * server flag for signed-in readers, or the persisted local record for
   * anonymous ones. A schedule subscription covers every event, so it counts
   * as "sent" for each row (the EmailMenu card control's rule).
   */
  hasExperienceEmailSubscription: boolean;
  viewedExperienceIds: Set<string>;
};

/**
 * The event was saved into this specific Agenda Hero destination (page,
 * planner, or schedule), via any source: the server's exportedToExperiences
 * (experience destinations, survives reload), the persisted local record
 * (detached-copy destinations the server can't trace), or this session's
 * optimistic record.
 */
export const isSavedToAgendaHeroDestination = (
  event: DecoratedEvent,
  destinationId: string,
  inputs: Pick<EventExportStatusInputs, 'platformExports' | 'hasLocalAdd'>
): boolean =>
  hasExperienceExport(event, destinationId) ||
  inputs.hasLocalAdd(event.id, PlatformTypes.AgendaHero, destinationId) ||
  inputs.platformExports.some(
    (item) =>
      item.platform === PlatformTypes.AgendaHero &&
      item.calendarId === destinationId &&
      item.entryExports.some((e) => e.entry.id === event.id)
  );

/**
 * THE per-event × platform "has this been added?" rule — one function, so the
 * cards, menus, and bulk controls can't disagree. Each destination module
 * declares which signal answers the question (statusSource); server data wins
 * where it exists, local records cover everything else:
 *
 * - integrationExports (Google/Outlook integrations) → the provider's
 *   `event.exports` rows, with the optimistic record as fallback until the
 *   server catches up.
 * - emailSubscription → the server's per-event or whole-schedule subscription
 *   flags, the persisted local record, or the optimistic record.
 * - experienceMembership (Agenda Hero) → `event.exportedToExperiences` minus
 *   the viewed schedule(s), persisted schedule-destination adds, or the
 *   optimistic record.
 * - localOnly (the weblinks, the feed, and any unregistered platform) →
 *   persisted local adds; these leave no server trace.
 */
export const getEventExportStatus = (
  event: DecoratedEvent,
  platform: PlatformTypes,
  inputs: EventExportStatusInputs,
  // Only the integrationExports branch reads this: a provider account holds
  // many calendars, so "added" can be asked per calendar there. The other
  // sources leave one record per platform.
  scope?: { calendarId?: string }
): ExportStatus => {
  const {
    platformExports,
    hasLocalAdd,
    didSubscribeToEvent,
    hasExperienceEmailSubscription,
    viewedExperienceIds,
  } = inputs;

  const statusSource = getDestination(platform)?.statusSource ?? 'localOnly';

  if (statusSource === 'integrationExports') {
    const exportPlatform = getIntegrationExportPlatform(platform);
    const serverStatus = exportPlatform
      ? getIntegrationExportStatus(event, exportPlatform, scope?.calendarId)
      : undefined;
    if (serverStatus?.isSuccess) {
      return serverStatus;
    }

    return {
      isSuccess: hasLocalExport(
        platformExports,
        event,
        platform,
        scope?.calendarId
      ),
      isStale: false,
    };
  }

  if (statusSource === 'emailSubscription') {
    return {
      isSuccess:
        // The server's flag survives new browsers/cleared storage; the local
        // records cover anonymous sends and the refetch gap.
        (isExperienceEvent(event) && event.isSubscribed) ||
        hasExperienceEmailSubscription ||
        hasLocalExport(platformExports, event, platform) ||
        didSubscribeToEvent(event.id),
      isStale: false,
    };
  }

  if (statusSource === 'experienceMembership') {
    return {
      isSuccess:
        hasExperienceExportElsewhere(event, viewedExperienceIds) ||
        hasLocalAdd(event.id, platform) ||
        hasLocalExport(platformExports, event, platform),
      isStale: false,
    };
  }

  return {
    isSuccess:
      hasLocalAdd(event.id, platform) ||
      hasLocalExport(platformExports, event, platform),
    isStale: false,
  };
};
